import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useDeliveryCatalog } from '@/api/domains/delivery-catalog';
import {
  useBulkUpsertExclusions,
  useExcludedProducts,
  useUpsertExclusion,
} from '@/api/domains/excluded-products';
import { useProductFacets, useShopifyProducts } from '@/api/domains/products';
import { BulkActionBar } from '@/components/bulk-action-bar';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { resolveLocale } from '@/i18n';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { BulkExclusionDialog } from './BulkExclusionDialog';
import { EditExclusionDialog } from './EditExclusionDialog';
import { ExclusionFilters } from './ExclusionFilters';
import { ExclusionsTable } from './ExclusionsTable';
import {
  countryOptionsFromCodes,
  EMPTY_EXCLUSION_FILTERS,
  isExclusionMode,
  numericProductId,
  type ExclusionFilterValues,
  type ExclusionRow,
  type MethodOption,
} from './lib';

/**
 * Capped at 200, not Shopify's 250: the overlay query echoes one product id per
 * visible row back through a GET query string, and 250 ids overflow nginx's
 * default 8k header buffer.
 */
const PER_PAGE_OPTIONS = [5, 10, 25, 50, 100, 200] as const;

/**
 * Hybrid catalog view: by default the table lists Shopify products (cursor
 * pagination) with their exclusion state overlaid. Applying an exclusion filter
 * (method/country/status) or "Show: Excluded" switches the source to the
 * merchant's exclusion records (offset pagination, stored titles) - see
 * `isExclusionMode`. The collection and tag filters only reach the Shopify query,
 * so the filter bar switches them off in that mode.
 */
export function ExcludedProductsPage() {
  const { t, i18n } = useTranslation('excluded-products');
  const locale = resolveLocale(i18n.language);
  usePageTitle(t('title'));

  // One applied set for the whole bar; the bar itself stages edits until Search.
  const [applied, setApplied] = useState<ExclusionFilterValues>(EMPTY_EXCLUSION_FILTERS);

  const [perPage, setPerPage] = useState<number>(25);
  const [page, setPage] = useState(1);
  // Shopify pagination is cursor-based: the stack holds the cursor trail so
  // "Previous" can pop back.
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<ExclusionRow | null>(null);
  // Per-product pending toggles - a single shared mutation object only tracks
  // its latest call, which mis-reports state when two rows are toggled quickly.
  const [pendingToggles, setPendingToggles] = useState<Set<string>>(new Set());

  const mode: 'catalog' | 'exclusions' = isExclusionMode(applied) ? 'exclusions' : 'catalog';

  const catalogQuery = useDeliveryCatalog();
  const upsert = useUpsertExclusion();
  const bulkUpsert = useBulkUpsertExclusions();

  // Full catalog (not just what's configured in markets) so exclusions can be
  // defined ahead of market setup.
  const methodOptions = useMemo<MethodOption[]>(
    () =>
      (catalogQuery.data?.methods ?? []).map((method) => ({
        methodKey: method.method_key,
        label: method.label,
      })),
    [catalogQuery.data],
  );

  // Individual countries - exclusions target single countries, e.g. only PL
  // out of a PL+IT market.
  const countryOptions = useMemo(
    () => countryOptionsFromCodes(catalogQuery.data?.countries ?? [], locale),
    [catalogQuery.data, locale],
  );

  const productsQuery = useShopifyProducts(
    {
      search: applied.search || null,
      per_page: perPage,
      cursor: cursorStack.at(-1) ?? null,
      collection_id: applied.collectionId,
      tag: applied.tag || null,
    },
    mode === 'catalog',
  );

  // One Shopify round trip when the page opens; both lists then filter in-browser.
  const facetsQuery = useProductFacets();
  const collectionOptions = useMemo(
    () =>
      (facetsQuery.data?.data.collections ?? []).map((collection) => ({
        value: collection.id,
        label: collection.title,
      })),
    [facetsQuery.data],
  );

  const catalogProducts = useMemo(() => productsQuery.data?.data ?? [], [productsQuery.data]);
  const catalogProductIds = useMemo(
    () => catalogProducts.map((product) => numericProductId(product.shopify_product_id)),
    [catalogProducts],
  );

  // Overlay query: exclusion records for exactly the visible catalog page.
  const overlayQuery = useExcludedProducts(
    { product_ids: catalogProductIds, per_page: Math.max(catalogProductIds.length, 1) },
    mode === 'catalog' && catalogProductIds.length > 0,
  );

  const exclusionsQuery = useExcludedProducts(
    {
      method_key: applied.methodKey,
      country: applied.country,
      status: applied.status,
      search: applied.search || null,
      page,
      per_page: perPage,
    },
    mode === 'exclusions',
  );

  const rows = useMemo<ExclusionRow[]>(() => {
    if (mode === 'exclusions') {
      return (exclusionsQuery.data?.data ?? []).map((exclusion) => ({
        productId: exclusion.shopify_product_id,
        title: exclusion.title,
        exclusion,
      }));
    }
    const exclusionsByProductId = new Map(
      (overlayQuery.data?.data ?? []).map((exclusion) => [exclusion.shopify_product_id, exclusion]),
    );
    return catalogProducts.map((product) => {
      const productId = numericProductId(product.shopify_product_id);
      return {
        productId,
        title: product.title,
        handle: product.handle,
        imageUrl: product.image_url,
        exclusion: exclusionsByProductId.get(productId) ?? null,
      };
    });
  }, [mode, exclusionsQuery.data, overlayQuery.data, catalogProducts]);

  const isLoading =
    mode === 'catalog'
      ? productsQuery.isLoading || (catalogProductIds.length > 0 && overlayQuery.isLoading)
      : exclusionsQuery.isLoading;
  const isError = mode === 'catalog' ? productsQuery.isError : exclusionsQuery.isError;

  const hasPrevious = mode === 'catalog' ? cursorStack.length > 0 : page > 1;
  const hasNext =
    mode === 'catalog'
      ? (productsQuery.data?.meta.has_next_page ?? false)
      : exclusionsQuery.data !== undefined && page < exclusionsQuery.data.meta.last_page;

  const resetPagination = useCallback(() => {
    setPage(1);
    setCursorStack([]);
    setSelected(new Set());
  }, []);

  const applyFilters = (next: ExclusionFilterValues) => {
    setApplied(next);
    resetPagination();
  };

  const changePerPage = (next: number) => {
    setPerPage(next);
    resetPagination();
  };

  // Selection is per page (select-all covers the visible rows), and a bulk action
  // can only resolve ids that are still in `rows` - so carrying a selection across
  // a page change would silently drop the off-page rows while the bar still
  // counted them.
  const goPrevious = () => {
    setSelected(new Set());
    if (mode === 'catalog') setCursorStack((stack) => stack.slice(0, -1));
    else setPage((current) => Math.max(1, current - 1));
  };

  const goNext = () => {
    setSelected(new Set());
    if (mode === 'catalog') {
      const cursor = productsQuery.data?.meta.end_cursor;
      if (cursor) setCursorStack((stack) => [...stack, cursor]);
    } else {
      setPage((current) => current + 1);
    }
  };

  const handleToggleStatus = useCallback(
    (row: ExclusionRow, isActive: boolean) => {
      setPendingToggles((previous) => new Set(previous).add(row.productId));
      upsert.mutate(
        { shopify_product_id: row.productId, title: row.title, is_active: isActive },
        {
          onSuccess: () => toast.success(t('saved')),
          onError: (error) => toastProblem(toApiProblem(error)),
          onSettled: () => {
            setPendingToggles((previous) => {
              const next = new Set(previous);
              next.delete(row.productId);
              return next;
            });
          },
        },
      );
    },
    [upsert, t],
  );

  const selectedRows = useMemo(
    () => rows.filter((row) => selected.has(row.productId)),
    [rows, selected],
  );
  // Status flips only make sense for rows that already carry an exclusion - writing
  // a record for an unexcluded product would just add noise to the list.
  const disableableRows = useMemo(
    () => selectedRows.filter((row) => row.exclusion?.is_active === true),
    [selectedRows],
  );
  const enableableRows = useMemo(
    () => selectedRows.filter((row) => row.exclusion?.is_active === false),
    [selectedRows],
  );

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  /**
   * Flips status only. Sending no scope keys makes each product keep the methods
   * and countries it already had, so disable/enable round-trips without
   * flattening a scoped exclusion into a blanket one.
   */
  const setSelectedStatus = (rowsToChange: ExclusionRow[], isActive: boolean) => {
    if (rowsToChange.length === 0) return;
    bulkUpsert.mutate(
      {
        products: rowsToChange.map((row) => ({
          shopify_product_id: row.productId,
          title: row.title,
        })),
        is_active: isActive,
      },
      {
        onSuccess: (result) => {
          toast.success(t(isActive ? 'bulk.enabled' : 'bulk.disabled', { count: result.total }));
          clearSelection();
        },
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  // Keep the dialog backed by the freshest query data (e.g. after a refetch
  // while it is open) instead of the snapshot captured on pencil click.
  const editingRow = editing
    ? (rows.find((row) => row.productId === editing.productId) ?? editing)
    : null;

  return (
    <div>
      <div className="space-y-6">
        <h2 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('title')}</h2>
        <p className="text-base text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="mt-9">
        <ExclusionFilters
          values={applied}
          onApply={applyFilters}
          methodOptions={methodOptions}
          countryOptions={countryOptions}
          collectionOptions={collectionOptions}
          collectionsTruncated={facetsQuery.data?.data.collections_truncated ?? false}
        />
      </div>

      <div className="mt-7 space-y-[18px]">
        <div className="relative">
          <ExclusionsTable
            rows={rows}
            isLoading={isLoading}
            isError={isError}
            skeletonRows={perPage}
            selected={selected}
            onSelectedChange={setSelected}
            onToggleStatus={handleToggleStatus}
            togglingIds={pendingToggles}
            onEdit={setEditing}
          />

          {/*
            Counted from `selectedRows`, not `selected`: a row can leave the page
            without any navigation - flipping one row's own status Switch
            invalidates the exclusions query, and under a status/method/country
            filter the refetched page no longer contains it. Reading the raw set
            here would show "3 selected" above buttons offering to act on 2, and
            would keep the bar mounted after every selected row had vanished.
          */}
          <BulkActionBar
            count={selectedRows.length}
            label={t('bulk.selected', { count: selectedRows.length })}
            onClear={clearSelection}
            clearLabel={t('bulk.clearSelection')}
          >
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={enableableRows.length === 0 || bulkUpsert.isPending}
              onClick={() => setSelectedStatus(enableableRows, true)}
            >
              {t('bulk.enable', { count: enableableRows.length })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={disableableRows.length === 0 || bulkUpsert.isPending}
              onClick={() => setSelectedStatus(disableableRows, false)}
            >
              {t('bulk.disable', { count: disableableRows.length })}
            </Button>
            <Button
              size="sm"
              type="button"
              disabled={selectedRows.length === 0}
              onClick={() => setBulkOpen(true)}
            >
              {t('bulk.trigger', { count: selectedRows.length })}
            </Button>
          </BulkActionBar>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm leading-5 text-black">{t('pagination.resultsPerPage')}</span>
            <Select value={String(perPage)} onValueChange={(value) => changePerPage(Number(value))}>
              <SelectTrigger className="w-[64px] text-muted-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_PAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" disabled={!hasPrevious} onClick={goPrevious}>
              {t('pagination.previous')}
            </Button>
            <Button variant="outline" disabled={!hasNext} onClick={goNext}>
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      </div>

      <BulkExclusionDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        rows={selectedRows}
        methodOptions={methodOptions}
        countryOptions={countryOptions}
        onApplied={clearSelection}
      />

      <EditExclusionDialog
        row={editingRow}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        methodOptions={methodOptions}
        countryOptions={countryOptions}
      />
    </div>
  );
}
